import intl from 'react-intl-universal';
import { PROGRAM_NUMBER_FORMAT, PROJECT_NUMBER_FORMAT } from './constants';
import { IdentifierFormat } from './customTypes';
import { RequestPriority } from './types/request';

export const formatProjectNumber = (projectNumber?: number | null) =>
  formatIdentifier(PROJECT_NUMBER_FORMAT, projectNumber);

export const formatProgramNumber = (programNumber?: number | null) =>
  formatIdentifier(PROGRAM_NUMBER_FORMAT, programNumber);

const formatIdentifier = (
  format: IdentifierFormat,
  identifier?: number | null
) => {
  if (!identifier) {
    return '';
  }
  const numberLength = identifier.toString().length;
  if (numberLength < 7) {
    return format.FULL.slice(0, format.FULL.length - numberLength) + identifier;
  } else {
    return format.BASE + identifier;
  }
};

export const formatDate = (date?: string): string =>
  date
    ? intl.get('DATES.SHORT_DATE', { date: new Date(date) })
    : intl.get('DATES.NO_DATE');

/**
 * Falls back to the "unassigned" label for any priority the locale file does
 * not define - including null/undefined, which would otherwise build the key
 * `...PRIORITY_OPTIONS.null`. The fallback must be a resolved message, not a
 * key path: `.d()` renders its argument verbatim. Callers pass optional fields
 * such as `Request.ldPriority` straight through, hence the optional parameter.
 */
export const formatRequestLearningPriority = (
  priority?: RequestPriority | null
): string =>
  intl
    .get(`REQUEST_PAGE.L_D_SECTION.PRIORITY_OPTIONS.${priority}`)
    .d(intl.get('REQUEST_PAGE.L_D_SECTION.PRIORITY_OPTIONS.unassigned'));

export const truncateText = (content: string, limit: number): string => {
  if (limit < 4) {
    throw new Error('Limit must be at least 4');
  }

  if (content.length <= limit) {
    return content;
  }

  return content.substring(0, limit - 3) + '...';
};

/**
 * Formats a number into a human-readable abbreviated format with appropriate suffixes (k, M, B, etc.)
 * @param value - The number to format
 * @returns Abbreviated formatted string (e.g., "1.01k", "2.5M", "999")
 */
export const formatNumberAbbreviated = (value: number): string => {
  const prefix = value < 0 ? '-' : '';
  const absValue = Math.abs(value);

  if (absValue < 1000) {
    return value.toString();
  }

  const suffixes = ['', 'k', 'M', 'B', 'T'];
  const magnitude = Math.floor(Math.log10(absValue) / 3);
  const scaledValue = absValue / Math.pow(1000, magnitude);

  // Round to 2 decimal places and remove trailing zeros
  const roundedValue = Math.round(scaledValue * 100) / 100;

  return `${prefix}${roundedValue}${suffixes[magnitude]}`;
};
